// Implements: F-UIX-016, F-UIX-007
/**
 * The Lienzo editor's split (UX-05, the mockup's `.editor`): the conversation on the left, the
 * preview on the neutral canvas on the right, and a draggable divider between them.
 *
 * The conversation starts at 35 % of the space, never narrower than 360 px nor wider than 480 px.
 * The divider is a focusable `role="separator"` with its value, so it is operable by keyboard
 * (arrows move 16 px, Home and End go to the bounds) and by pointer (drag), and a double click
 * resets it. The chosen width is remembered per viewer under one key; storage is a convenience
 * wrapped in try/catch, never a dependency (checklist 15). Collapsed, the conversation steps aside to a
 * narrow rail that holds the way back (owner report, round 3): it stays MOUNTED and only hidden, so
 * a turn in flight, its stream and the typed text survive; the divider goes; the sidebar is not
 * touched. Below 1024 px the two stack, conversation on top, and
 * there is no divider, as in the mockup; the document order is the same at every width.
 *
 * The width reaches the CSS through `style.setProperty` (the CSP bans `style=""`).
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent, ReactElement, ReactNode } from "react";

import { useTranslation } from "../../i18n";
import { readPanelWidth, writePanelWidth } from "../panelMemory";
import { useMediaQuery } from "../useMediaQuery";
import styles from "./EditorWorkspace.module.css";

export const CONVERSATION_MIN = 360;
export const CONVERSATION_MAX = 480;
/** The share of the editor the conversation starts with. */
const CONVERSATION_SHARE = 0.35;
const KEY_STEP = 16;
/** The panel id the width is remembered under (`prototyper.panel.<id>`, `panelMemory`). */
export const CONVERSATION_PANEL_ID = "editor-conversation";
/** The mockup's stacking breakpoint. */
export const STACKED_QUERY = "(max-width: 1023.98px)";

export interface EditorWorkspaceProps {
  readonly conversation: ReactNode;
  readonly preview: ReactNode;
  /** The conversation is collapsed to the rail; the divider steps aside. */
  readonly collapsed: boolean;
  /** What the rail holds while collapsed: the way back and what happened meanwhile. */
  readonly rail: ReactNode;
}

function clamp(width: number): number {
  return Math.min(CONVERSATION_MAX, Math.max(CONVERSATION_MIN, Math.round(width)));
}

/** The remembered width, or `null` when none (or not a valid one) is stored, or storage refuses. */
function readWidth(): number | null {
  return readPanelWidth(CONVERSATION_PANEL_ID, CONVERSATION_MIN, CONVERSATION_MAX);
}

function writeWidth(width: number | null): void {
  writePanelWidth(CONVERSATION_PANEL_ID, width);
}

interface Drag {
  readonly pointer: number;
  readonly x: number;
  readonly width: number;
}

export function EditorWorkspace({ conversation, preview, collapsed, rail }: EditorWorkspaceProps): ReactElement {
  const { t } = useTranslation();
  const stacked = useMediaQuery(STACKED_QUERY);
  const workspace = useRef<HTMLDivElement | null>(null);
  const [chosen, setChosen] = useState<number | null>(readWidth);
  const [fallback, setFallback] = useState(CONVERSATION_MIN);
  const [drag, setDrag] = useState<Drag | null>(null);
  const width = chosen ?? fallback;

  // The default follows the space the editor has; it is measured before the first paint.
  useLayoutEffect(() => {
    const element = workspace.current;
    if (element !== null) setFallback(clamp(element.clientWidth * CONVERSATION_SHARE));
  }, []);

  useLayoutEffect(() => {
    workspace.current?.style.setProperty("--conversation-width", `${String(width)}px`);
  }, [width]);

  const choose = useCallback((next: number, remember: boolean): void => {
    const bounded = clamp(next);
    setChosen(bounded);
    if (remember) writeWidth(bounded);
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const next =
      event.key === "ArrowLeft"
        ? width - KEY_STEP
        : event.key === "ArrowRight"
          ? width + KEY_STEP
          : event.key === "Home"
            ? CONVERSATION_MIN
            : event.key === "End"
              ? CONVERSATION_MAX
              : null;
    if (next === null) return;
    event.preventDefault();
    choose(next, true);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    setDrag({ pointer: event.pointerId, x: event.clientX, width });
    if (typeof event.currentTarget.setPointerCapture === "function") {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (drag?.pointer !== event.pointerId) return;
    choose(drag.width + event.clientX - drag.x, false);
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    if (drag?.pointer !== event.pointerId) return;
    setDrag(null);
    choose(drag.width + event.clientX - drag.x, true);
  };

  const showDivider = !collapsed && !stacked;
  return (
    <div
      ref={workspace}
      className={styles.workspace}
      data-testid="editor-workspace"
      data-layout={stacked ? "stacked" : "split"}
      data-collapsed={collapsed ? "true" : undefined}
    >
      {collapsed ? (
        <div className={styles.rail} data-testid="editor-rail">
          {rail}
        </div>
      ) : null}
      <div className={styles.conversation} hidden={collapsed}>
        {conversation}
      </div>
      {showDivider ? (
        /* The WAI-ARIA window splitter: a focusable separator with a value is a widget, which the
           a11y lint rules (written for the static separator) do not know. */
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
        <div
          className={styles.divider}
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label={t("editor.divider.label")}
          aria-valuemin={CONVERSATION_MIN}
          aria-valuemax={CONVERSATION_MAX}
          aria-valuenow={width}
          data-dragging={drag === null ? undefined : "true"}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            setDrag(null);
          }}
          onDoubleClick={() => {
            setChosen(null);
            writeWidth(null);
          }}
        />
      ) : /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */ null}
      <div className={styles.preview}>{preview}</div>
    </div>
  );
}
